import {
  AvailableSchedulePeriod,
  CalendarAvailableBlock,
  CalendarTimeSlot,
  ITimeSlotTimestamp,
  mergeContiguous,
  SELECT_TIME_INCREMENT,
  toBlockSlot,
} from '@techspert-io/schedule-calendar';
import { InterviewPeriod } from '../../../../../../features/interview-period/models/interview-period.models';

export const MAX_BEST_MATCHES = 3;

const MILLISECONDS_PER_MINUTE = 60_000;

export interface IBestMatch {
  slot: CalendarTimeSlot;
  /** Preferred by the expert for the whole of the time */
  isExpertPreferred: boolean;
  /** Preferred in the interview period for the whole of the time */
  isClientPreferred: boolean;
}

type Candidate = IBestMatch & {
  block: CalendarAvailableBlock;
  start: number;
  end: number;
  priority: number;
};

interface ITimeRange {
  start: number;
  end: number;
}

const toTimeRange = ({ start, end }: ITimeSlotTimestamp): ITimeRange => ({
  start: Date.parse(start),
  end: Date.parse(end),
});

/** Continuous ranges, so a time across touching periods sits within one */
const toCoveringRanges = (periods: ITimeSlotTimestamp[]) =>
  mergeContiguous(periods).map(toTimeRange);

const isCovered = (ranges: ITimeRange[], { start, end }: ITimeRange) =>
  ranges.some((range) => range.start <= start && range.end >= end);

const overlaps = (a: ITimeRange, b: ITimeRange) =>
  a.start < b.end && a.end > b.start;

/**
 * Both preferred first, then the expert preferred, then the client preferred,
 * then neither for the whole of the time
 */
const toPriority = ({ isExpertPreferred, isClientPreferred }: IBestMatch) => {
  if (isExpertPreferred && isClientPreferred) {
    return 0;
  }

  if (isExpertPreferred) {
    return 1;
  }

  if (isClientPreferred) {
    return 2;
  }

  return 3;
};

const byPriorityThenSoonest = (a: Candidate, b: Candidate) =>
  a.priority - b.priority || a.start - b.start;

/**
 * Up to three times the expert and the interview period are both available
 * for, clear of the project's calls, by priority then soonest. Times start on
 * the increments from the start of the expert's blocks that they can be
 * selected on, from now.
 *
 * Each match comes from a different block where it can, so they aren't all
 * taken from one long preferred block, and matches never overlap.
 */
export const findBestMatches = ({
  blocks,
  duration,
  schedule,
  interviewPeriod,
  now,
}: {
  /** The expert's available blocks on the days shown */
  blocks: CalendarAvailableBlock[];
  /** Minutes long of the times */
  duration: number;
  /** The expert's available times */
  schedule: AvailableSchedulePeriod[];
  interviewPeriod: InterviewPeriod;
  now: Date;
}): IBestMatch[] => {
  const expertPreferred = toCoveringRanges(
    schedule.filter((p) => p.isPreferred)
  );
  const clientAvailable = toCoveringRanges(interviewPeriod.available);
  const clientPreferred = toCoveringRanges(
    interviewPeriod.available.filter((p) => p.isPreferred)
  );
  const calls = interviewPeriod.call.map(toTimeRange);

  // From the next minute, as the calendar allows selecting from, so a time
  // offered stays in the future until the clock next moves on
  const earliest =
    (Math.floor(now.getTime() / MILLISECONDS_PER_MINUTE) + 1) *
    MILLISECONDS_PER_MINUTE;

  const candidates = blocks
    .flatMap((block) =>
      Array.from(
        {
          length: Math.max(
            0,
            Math.floor(
              (block.end - block.start - duration) / SELECT_TIME_INCREMENT
            ) + 1
          ),
        },
        (_, i) => {
          const slot = toBlockSlot(
            block,
            block.start + i * SELECT_TIME_INCREMENT,
            duration
          );
          const range = toTimeRange(slot.range);
          const match: IBestMatch = {
            slot,
            isExpertPreferred: isCovered(expertPreferred, range),
            isClientPreferred: isCovered(clientPreferred, range),
          };

          return { ...match, ...range, block, priority: toPriority(match) };
        }
      )
    )
    .filter(
      (candidate) =>
        candidate.start >= earliest &&
        isCovered(clientAvailable, candidate) &&
        !calls.some((call) => overlaps(call, candidate))
    )
    .sort(byPriorityThenSoonest);

  const picked: Candidate[] = [];

  const pick = (canUse: (candidate: Candidate) => boolean) =>
    candidates.forEach((candidate) => {
      if (
        picked.length < MAX_BEST_MATCHES &&
        canUse(candidate) &&
        !picked.some((match) => overlaps(match, candidate))
      ) {
        picked.push(candidate);
      }
    });

  pick((candidate) => !picked.some(({ block }) => block === candidate.block));
  pick((candidate) => !picked.includes(candidate));

  return picked
    .sort(byPriorityThenSoonest)
    .map(({ slot, isExpertPreferred, isClientPreferred }) => ({
      slot,
      isExpertPreferred,
      isClientPreferred,
    }));
};
